import {
  QueryClient,
  QueryKey,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import cx from 'classnames';
import cookies from 'js-cookie';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { memo, useEffect, useMemo, useState } from 'react';

import { loadShippingLocations } from '../components/modules/shipping-selector';
import ProductPage from '../components/pages/product';
import { useAppContext } from '../components/providers/app-context';
import { setShippingLocation } from '../services/shipping-locations/set';
import {
  ADVANCED_VIEW_ACTIVE,
  DETAILED_PRICES,
  SHIPPING_LOCATION,
} from '../static/cookies';
import searchCategoryName from '../utils/get-category-names';
import { isGlobalUser } from '../utils/is-global-user';
import { isUKuser, ukRegions } from '../utils/is-uk-user';
import { isUSuser } from '../utils/is-us-user';
import { kmsToMiles } from '../utils/kms-to-miles';
import getGroups from '../utils/react-query/query-options/get-groups';
import getLoggedUser from '../utils/react-query/query-options/get-logged-user';
import getProductDetails from '../utils/react-query/query-options/get-product-details';
import getProductUniqueVehicle from '../utils/react-query/query-options/get-product-unique-vehicle';
import getRelatedProducts from '../utils/react-query/query-options/get-related-products';
import getStock from '../utils/react-query/query-options/get-stocks';
import getTranslatedSlugs from '../utils/react-query/query-options/get-translated-slugs';
import { redirectForAdvanced } from '../utils/redirect-for-advanced';
import slugChange301 from '../utils/slug-change-301';
import validateCategoryPartSlugs from '../utils/validate-category-part-slugs';
import forceShippingUKForCoUK from '../utils/force-shipping-uk-for-co-uk';

const useData = () => {
  const {
    locale = 'en',
    query: { category = '', subCategory = '' },
  } = useRouter();
  const { detailedPrice } = useAppContext();
  const [firstRender, setFirstRender] = useState(true);
  const slug = subCategory as string;
  const csrftoken = cookies.get('csrftoken') || '';

  const {
    data: productDetail,
    isFetching: isProductDetailsLoading,
    isFetched: isFetchedProductDetail,
    refetch: refetchProductDetail,
  } = useQuery<unknown, unknown, TProductDetail, QueryKey>(
    ...getProductDetails({
      locale,
      slug,
      detailed_prices: !!detailedPrice,
    }),
    {
      keepPreviousData: true,
    }
  );

  let milageCoUK;
  if (
    productDetail?.mileage &&
    process.env.NEXT_PUBLIC_HOST?.includes('.co.uk')
  ) {
    milageCoUK = kmsToMiles(productDetail?.mileage);
    productDetail.milageCoUK = milageCoUK;
  }

  const { data: translatedSlugs } = useQuery(
    ...getTranslatedSlugs({
      lang_origin: locale,
      lang_trans: null,
      slugs: [
        {
          slug_origin: category as string,
          slug_type: 'category',
        },
        {
          slug_origin: subCategory as string,
          slug_type: 'product',
        },
      ],
    }),
    {
      keepPreviousData: true,
    }
  );

  const {
    data: relatedProducts,
    isFetching: isRelatedProductsLoading,
    isFetched: isRelatedProductsLoaded,
  } = useQuery<unknown, unknown, Array<TProductListItem>, QueryKey>(
    ...getRelatedProducts({ locale, slug, detailed_prices: !!detailedPrice }),
    {
      keepPreviousData: true,
    }
  );

  const {
    data: uniqueVehicle,
    isFetching: isUniqueVehicleLoading,
    isFetched: isUniqueVehicleLoaded,
  } = useQuery<unknown, unknown, TProductUniqueVehicle, QueryKey>(
    ...getProductUniqueVehicle({ locale, slug, detailed_prices: !!detailedPrice }),
    {
      keepPreviousData: true,
    }
  );

  // GTM
  useEffect(() => {
    if (isFetchedProductDetail && firstRender) {
      setFirstRender(false);
      // @ts-ignore
      window.dataLayer.push({
        event: 'eec.detail',
        ecommerce: {
          currencyCode: 'EUR', // currency code
          detail: {
            products: [
              {
                id: productDetail?.sku, // SKU of the product
                name: productDetail?.part_en, // name of the product.
                brand: productDetail?.car.brand_name, // brand of the part
                category: productDetail?.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
                price: productDetail?.pricing?.price_product
                  ? Number(productDetail.pricing.price_product).toFixed(2)
                  : null, // price of the part without shipping, tax and currency
                dimension2: productDetail?.reference, // reference of the product
                dimension3: productDetail?.car.model_name, // car model of the part
                dimension4: productDetail?.car?.version_name, // car version of the part
              },
            ],
          },
        },
      });

      // GA4
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'view_item',
        ecommerce: {
          currency: 'EUR',
          items: [
            {
              item_name: productDetail?.part_en,
              item_id: productDetail?.sku,
              price: productDetail?.pricing?.price_product
                ? Number(productDetail.pricing.price_product).toFixed(2)
                : null,
              item_brand: productDetail?.car.brand_name,
              item_category: productDetail?.category_en,
              item_category2: productDetail?.part_en,
              //item_list_name: 'Search Results',
              //item_list_id: 'search-results',
              index: 0, // hardcoded to zero in this event
              quantity: '1',
            },
          ],
        },
      });
    }
  }, [productDetail, isFetchedProductDetail]);

  // GTM
  useEffect(() => {
    if (isRelatedProductsLoaded && relatedProducts?.length > 0) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'eec.impressionView',
        ecommerce: {
          currencyCode: 'EUR',
          impressions: relatedProducts?.map((item, index) => ({
            list: 'Related Products', // List name where the impression was viewed
            id: item.sku, // SKU of the product
            name: item.part_en, // name of the product.
            brand: item.car_brand, // car brand of the part
            category: item.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
            price: item.pricing?.price_product
              ? Number(item.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            position: index, // the position of the item in the list
            dimension2: item.reference, // reference of the product
            dimension3: item.car_model, // car model of the part
            dimension4: item?.car_version, // car version of the part
          })),
        },
      });

      // GA4
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency: 'EUR', // Currency of the items associated with the event, in 3-letter ISO 4217 format
          items: relatedProducts?.map((item, index) => ({
            item_list_name: 'Related Products', // List name where the impression was viewed
            item_list_id: 'related-products', // List name where the impression was viewed
            item_id: item.sku, // SKU of the product
            item_name: item.part_en, // name of the product.
            item_brand: item.car_brand, // car brand of the part
            item_category: item.category_en, // Product category(ies) - each "/" is a category level (max 5 levels)
            item_category2: item.part_en, // part name === product name
            price: item.pricing?.price_product
              ? Number(item.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            index: index, // the position of the item in the list
            quantity: '1', // Hardcoded to 1
          })),
        },
      });
    }
  }, [relatedProducts, isRelatedProductsLoaded]);

  const { data: groups, isFetching: isgroupsLoading } = useQuery<
    unknown,
    unknown,
    TCategories,
    QueryKey
  >(...getGroups({ locale }), {
    keepPreviousData: true,
  });

  const categoriesInfo =
    productDetail &&
      groups &&
      productDetail.part.subgroup_slug &&
      productDetail.part.slug
      ? searchCategoryName(
        {
          category: productDetail.part.subgroup_slug,
          subCategory: productDetail.part.slug,
        },
        groups
      )
      : undefined;

  return {
    groups,
    productDetail,
    refetchProductDetail,
    relatedProducts,
    uniqueVehicle,
    categoriesInfo,
    isLoading:
      isProductDetailsLoading || isRelatedProductsLoading || isgroupsLoading || isUniqueVehicleLoading,
    translatedSlugs,
  };
};

const Product = memo(() => {
  const data = useData();

  return (
    <main className={cx({ 'opacity-75': data.isLoading })}>
      {data.productDetail !== undefined ? <ProductPage {...data} /> : null}
    </main>
  );
});

Product.displayName = 'Product';

export default Product;

export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en',
  req,
  res,
  query,
}) => {
  isUSuser(req, res);
  isUKuser(req, res);
  isGlobalUser(req, res);

  const {
    category = '',
    subCategory,
    shipping_location = '',
    searchSubCategory = '',
    detail_prices,
    ...restQuery
  } = query;

  const slug = subCategory as string;
  const { default: getGenericClient } = await import('../server/client');
  const genericClient = getGenericClient();
  const detail_prices_cookie = req.cookies.detail_prices;

  if (shipping_location) {
    // Force shipping location when the query param is present
    try {
      // Set shipping location via API
      await setShippingLocation({
        locale,
        shipping_location: shipping_location as string,
        csrftoken: req.cookies.csrftoken as string,
      });

      // Set cookie
      res.appendHeader(
        'set-cookie',
        `_shipping_location=${shipping_location}; Path=/`
      );
    } catch (e) {
      // If the shipping location is not valid, force set to 7 - Others

      // change to 404
      if (e.status >= 400 && e.status < 500) {
        const newUrl =
          (locale != 'en' ? `/${locale}` : '') +
          req.url?.replace(
            `shipping_location=${shipping_location}`,
            'shipping_location=7'
          );
        return {
          redirect: {
            destination: newUrl,
            permanent: false,
          },
        };
      }
    }
  } else {
    await forceShippingUKForCoUK({ req, res, locale });
  }

  if (detail_prices) {
    // Set cookie for detailed prices if the query param is present
    res.appendHeader('set-cookie', [
      `${shipping_location
        ? `_shipping_location=${shipping_location}; Path=/;`
        : ''
      } `,
      `${DETAILED_PRICES}=${detail_prices}; Path=/; Expires=${new Date(
        Date.now() + 1000 * 60 * 60 * 24
      ).toUTCString()}`,
    ]);
  }

  try {
    const [user, groups, productDetail] = await Promise.all([
      genericClient.fetchQuery(...getLoggedUser({ locale }, req)),
      genericClient.fetchQuery(...getGroups({ locale }, req)),
      genericClient.fetchQuery(
        ...getProductDetails(
          {
            locale,
            slug,
            shipping_location: shipping_location as string,
            detailed_prices:
              !!detail_prices || !!detail_prices_cookie,
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getTranslatedSlugs(
          {
            lang_origin: locale,
            lang_trans: null,
            slugs: [
              {
                slug_origin: category as string,
                slug_type: 'category',
              },
              {
                slug_origin: subCategory as string,
                slug_type: 'product',
              },
            ],
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getRelatedProducts(
          {
            locale,
            slug,
            // If we are in .co.uk, and the country region is not part of UK, force set to 12 - UK
            shipping_location: shipping_location as string,
            detailed_prices:
              !!detail_prices || !!detail_prices_cookie,
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ['user-dependent', 'shipping-locations-list', locale, parseInt((shipping_location || req.cookies[SHIPPING_LOCATION]) as string, 10)],
        (keys) => loadShippingLocations(keys, req)
      ),
    ]);

    if (productDetail.part.subgroup_slug !== category) {
      return slugChange301({
        locale,
        query: {
          ...query,
          category: productDetail.part.subgroup_slug,
          subCategory: productDetail.slug,
        },
        res,
        page: 'product',
      });
    }

    await validateCategoryPartSlugs({
      locale,
      groups,
      category: category as string,
      req,
    });

    const { default: retrieveStateAndClear } = await import(
      '../utils/retrieve-state-and-clear'
    );

    if (
      user?.staff?.is_pro &&
      !!parseInt(req.cookies[ADVANCED_VIEW_ACTIVE] || '0')
    ) {
      return redirectForAdvanced({
        locale,
        category: category as string,
        ...(!!searchSubCategory
          ? { subCategory: searchSubCategory as string }
          : {}),
        product: slug as string,
        otherQuery: restQuery,
      });
    }

    return {
      props: {
        dehydratedState: retrieveStateAndClear(genericClient),
        shippingLocation: parseInt(
          // If we are in .co.uk, and the country region is not part of UK, force set to 12 - UK
          (shipping_location
            ? shipping_location
            : req.cookies[SHIPPING_LOCATION]) as string,
          10
        ),
        detailPrices:
          (detail_prices as string) || req.cookies[DETAILED_PRICES] || null,
      },
    };
  } catch (e) {
    // https://localhost:3000/fr/pieces-automobiles/non-identifie/arbre-de-transmission-bmw-1-f20-116-d-26107624182-2011-2012-2013-2014-2015-2016-2017-2018-2019-18231950?shipping_location=11&detail_prices=true
    if (e.status === 301) {
      const newQuery: { [key: string]: string } = {
        ...query,
        ...(e.data.category && { category: e.data.category }),
        ...(e.data.product && { subCategory: e.data.product }),
      };

      return slugChange301({
        locale,
        query: newQuery,
        res,
        page: 'product',
      });
    } else if (e.status === 404) {
      return { notFound: true };
    }

    console.log(e);
    throw e;
  }
};
